import { useState, useEffect, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Check, Pin,
  AtSign, MessageSquare, UserPlus, UserMinus, CheckSquare,
  RotateCcw, Clock, FilePlus, AlertTriangle, FileEdit, FileText,
  X as XIcon, TrendingUp, ChevronDown, Video, Share2, Trash2, Search, History, Layers,
} from "lucide-react";
import {
  useUnreadCount,
  useNotifications,
  useNotificationsFullHistory,
  useMarkRead,
  useMarkAllRead,
  useTogglePin,
} from "@/features/tasks/hooks/useNotifications";
import {
  NOTIFICATION_CATEGORY_LABELS,
  NOTIFICATION_CATEGORY_ORDER,
  notificationLabel,
  type NotificationCategory,
  type NotificationTab,
} from "@/features/tasks/types";
import { useMyPricingNotifications, useMarkPricingNotificationRead, useTogglePricingPin } from "@/features/tenders/hooks/useTenders";
import type { MyPricingNotification } from "@/features/tenders/types";
import { useAuthStore } from "@/store/auth";
import { useNotificationSync } from "@/features/tasks/hooks/useNotificationSync";
import { formatAmount } from "@/utils/formatAmount";
import {
  acceptanceContextLine,
  acceptanceWarningLine,
  type AcceptanceNotificationExtra,
} from "@/features/tasks/acceptanceNotifications";

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "przed chwilą";
  if (diff < 3600) return `${Math.floor(diff / 60)} min temu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} godz. temu`;
  return `${Math.floor(diff / 86400)} dni temu`;
}

// Powiadomienia o tym samym miejscu backend scala w jedną pozycję — tu tylko
// dopisek „· 3 zdarzenia" z polską odmianą liczebnika.
function eventCountLabel(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  const plural =
    lastTwo >= 12 && lastTwo <= 14 ? "zdarzeń" : last >= 2 && last <= 4 ? "zdarzenia" : "zdarzeń";
  return `${count} ${plural}`;
}

type LucideIcon = React.FC<{ size?: number; className?: string }>;

const TYPE_ICON: Record<string, LucideIcon> = {
  mentioned: AtSign,
  chat_mentioned: AtSign,
  comment_added: MessageSquare,
  order_comment_new: MessageSquare,
  assigned: UserPlus,
  unassigned: UserMinus,
  subtask_done: CheckSquare,
  // Ponaglenie KPI („kpi_task_ping") celowo BEZ wpisu: domyślną ikoną tej mapy jest już
  // Bell (patrz `TYPE_ICON[...] ?? Bell`), a każdy wpis dokłada zastany błąd typów lucide.
  task_closed: CheckSquare,
  status_changed: RotateCcw,
  due_soon: Clock,
  task_created: FilePlus,
  order_deadline_soon: Clock,
  order_deadline_today: AlertTriangle,
  order_overdue: AlertTriangle,
  order_updated: FileEdit,
  order_access_granted: FileEdit,
  order_cancelled: XIcon,
  order_acceptance_warning: Clock,
  order_deleted: XIcon,
  order_change_request: FileEdit,
  order_change_approved: CheckSquare,
  order_change_rejected: XIcon,
  pock_request_submitted: FileText,
  pock_request_approved: CheckSquare,
  pock_request_rejected: XIcon,
  pock_accepted: CheckSquare,
  pock_rejected: XIcon,
  pock_orderer_acceptance_required: FileText,
  pock_orderer_accepted: CheckSquare,
  pock_orderer_rejected: XIcon,
  order_result_file_uploaded: FilePlus,
  order_result_file_rejected: XIcon,
  mdcp_photos_uploaded: FilePlus,
  mdcp_photos_rejected: XIcon,
  led_tile_activity: Layers,
  led_tile_gk_activity: Layers,
  invoice_submitted: FileText,
  invoice_accepted: CheckSquare,
  invoice_rejected: XIcon,
  leave_approved: CheckSquare,
  leave_rejected: XIcon,
  remote_work_approved: CheckSquare,
  remote_work_rejected: XIcon,
  issue_resolved: CheckSquare,
  issue_wont_fix: XIcon,
  issue_reopened: RotateCcw,
  issue_comment_added: MessageSquare,
  prz_proforma_paid: FileText,
  official_fee_submitted: FileText,
  official_fee_approved: CheckSquare,
  official_fee_rejected: XIcon,
  official_fee_payment_uploaded: CheckSquare,
  official_fee_acknowledged: CheckSquare,
  official_fee_attachment_added: FilePlus,
  application_approval_requested: FileText,
  application_approval_rejected: XIcon,
  application_approval_granted: CheckSquare,
  wypis_ordering_reminder: Clock,
  ze_report_due_soon: Clock,
  project_coordinator_assigned: UserPlus,
  wpw_attachments_updated: FileText,
  employee_access_expired_contract: AlertTriangle,
  supply_item_submitted: FilePlus,
  supply_item_approved: CheckSquare,
  meeting_invited: Video,
  meeting_updated: Video,
  meeting_cancelled: XIcon,
  meeting_reminder: Clock,
  meeting_started: Video,
  eng_review_shared: FileText,
  eng_review_decided: CheckSquare,
  two_drive_shared: Share2,
  two_drive_item_added: FilePlus,
  two_drive_item_removed: Trash2,
  two_drive_item_changed: FileEdit,
  lead_reply_received: MessageSquare,
  // Brak środków na koncie SMSAPI celowo BEZ wpisu (jak „kpi_task_ping" wyżej):
  // domyślny Bell na czerwono wystarczy, a każdy wpis dokłada zastany błąd typów lucide.
};

const TYPE_ICON_COLOR: Record<string, string> = {
  subtask_done: "text-emerald-600",
  task_closed: "text-emerald-600",
  order_change_approved: "text-emerald-600",
  pock_request_approved: "text-emerald-600",
  invoice_accepted: "text-emerald-600",
  leave_approved: "text-emerald-600",
  remote_work_approved: "text-emerald-600",
  supply_item_approved: "text-emerald-600",
  issue_resolved: "text-emerald-600",
  order_change_rejected: "text-[var(--color-primary)]",
  pock_request_rejected: "text-[var(--color-primary)]",
  pock_rejected: "text-[var(--color-primary)]",
  pock_orderer_rejected: "text-[var(--color-primary)]",
  invoice_rejected: "text-[var(--color-primary)]",
  leave_rejected: "text-[var(--color-primary)]",
  remote_work_rejected: "text-[var(--color-primary)]",
  issue_wont_fix: "text-[var(--color-primary)]",
  mdcp_photos_rejected: "text-[var(--color-primary)]",
  order_deadline_today: "text-[var(--color-primary)]",
  order_overdue: "text-[var(--color-primary)]",
  official_fee_approved: "text-emerald-600",
  official_fee_payment_uploaded: "text-emerald-600",
  official_fee_rejected: "text-[var(--color-primary)]",
  wypis_ordering_reminder: "text-amber-500",
  ze_report_due_soon: "text-amber-500",
  employee_access_expired_contract: "text-amber-500",
  supply_item_submitted: "text-amber-500",
  due_soon: "text-amber-500",
  order_acceptance_warning: "text-amber-500",
  order_deadline_soon: "text-amber-500",
  prz_proforma_paid: "text-amber-600",
  chat_mentioned: "text-[var(--color-primary)]",
  two_drive_item_removed: "text-[var(--color-primary)]",
  two_drive_item_added: "text-emerald-600",
  lead_reply_received: "text-emerald-600",
  sms_account_low_balance: "text-red-600",
};

// Nagłówki dni w liście — makieta ze zgłoszenia („Today / Yesterday / Last Week").
function dayBucket(iso: string): string {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const stamp = new Date(iso).getTime();
  if (stamp >= startOfToday) return "Dziś";
  if (stamp >= startOfToday - 86_400_000) return "Wczoraj";
  return "Wcześniej";
}

const TABS: { key: NotificationTab; label: string }[] = [
  { key: "all", label: "Wszystkie" },
  ...NOTIFICATION_CATEGORY_ORDER.map((category) => ({
    key: category as NotificationTab,
    label: NOTIFICATION_CATEGORY_LABELS[category],
  })),
];

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // How many notifications are rendered at once; grows via the "load more" button.
  const PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Otwarta zakładka. Celowo NIE zapamiętywana między otwarciami — zaparkowanie na
  // jednej zakładce ukrywałoby resztę, czyli dokładnie problem, który naprawiamy.
  const [tab, setTab] = useState<NotificationTab>("all");
  const [search, setSearch] = useState("");
  // Dzwonek dostaje okno (nieprzeczytane + przypięte + 30 dni); starsze na klik.
  const [fullHistory, setFullHistory] = useState(false);
  const currentUser = useAuthStore((s) => s.user);
  const isInternal = currentUser?.role !== "contractor";

  useNotificationSync(isInternal);

  const { data: countData } = useUnreadCount();
  // Listy tylko przy otwartym dzwonku — zamknięty pokazuje same liczby (unread_count).
  const { data: windowFeed } = useNotifications(open);
  const { data: fullFeed, isFetching: fullHistoryLoading } = useNotificationsFullHistory(fullHistory);
  const feed = fullHistory ? fullFeed ?? windowFeed : windowFeed;
  const notifications = feed?.items ?? [];
  const olderCount = windowFeed?.olderCount ?? 0;
  const { mutate: markRead } = useMarkRead();
  const { mutate: markAllRead } = useMarkAllRead();
  const { mutate: togglePin } = useTogglePin();

  const { data: pricingNotifs = [] } = useMyPricingNotifications(undefined, isInternal && open);
  const { mutate: markPricingRead } = useMarkPricingNotificationRead();
  const { mutate: togglePricingPin } = useTogglePricingPin();

  const unreadCount = (countData?.count ?? 0) + (countData?.pricing_unread ?? 0);

  // Collapse back to the first page whenever the panel is closed; zakładka,
  // wyszukiwarka i archiwum wracają do stanu wyjściowego razem z nią.
  useEffect(() => {
    if (!open) {
      setVisibleCount(PAGE_SIZE);
      setTab("all");
      setSearch("");
      setFullHistory(false);
    }
  }, [open]);

  // Zmiana zakładki albo frazy zaczyna listę od początku.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [tab, search]);

  type UnifiedNotif =
    | { kind: "task"; category: NotificationCategory; data: (typeof notifications)[0] }
    | { kind: "pricing"; category: NotificationCategory; data: MyPricingNotification };

  const sortedUnified: UnifiedNotif[] = [
    ...notifications.map((n) => ({
      kind: "task" as const,
      // Backend liczy kategorię z całej scalonej pozycji; stary wiersz bez pola
      // ląduje w „Inne", więc nigdy nie wypada ze wszystkich zakładek.
      category: n.category ?? ("other" as NotificationCategory),
      data: n,
    })),
    ...pricingNotifs.map((n: MyPricingNotification) => ({
      kind: "pricing" as const,
      // Wyceny to drugi strumień dzwonka — bez kategorii zniknęłyby z zakładek.
      category: "other" as NotificationCategory,
      data: n,
    })),
  ].sort((a, b) => {
    const aPinned = a.data.is_pinned ? 1 : 0;
    const bPinned = b.data.is_pinned ? 1 : 0;
    if (bPinned !== aPinned) return bPinned - aPinned;
    // Scalona pozycja plasuje się według najnowszego ze swoich zdarzeń.
    const at = a.kind === "task" ? a.data.group_latest_at ?? a.data.created_at : a.data.created_at;
    const bt = b.kind === "task" ? b.data.group_latest_at ?? b.data.created_at : b.data.created_at;
    return new Date(bt).getTime() - new Date(at).getTime();
  });

  // Liczniki zakładek: szary = wszystko wczytane, czerwony = nieprzeczytane.
  // Liczone przed filtrem wyszukiwarki, żeby fraza nie „gubiła" pozycji z licznika.
  const tabCounts: Record<NotificationTab, { total: number; unread: number }> = {
    all: { total: 0, unread: 0 },
    me: { total: 0, unread: 0 },
    approval: { total: 0, unread: 0 },
    deadline: { total: 0, unread: 0 },
    system: { total: 0, unread: 0 },
    other: { total: 0, unread: 0 },
  };
  for (const item of sortedUnified) {
    const isUnread = !item.data.is_read;
    tabCounts.all.total += 1;
    tabCounts[item.category].total += 1;
    if (isUnread) {
      tabCounts.all.unread += 1;
      tabCounts[item.category].unread += 1;
    }
  }

  const query = search.trim().toLowerCase();
  const matchesQuery = (item: UnifiedNotif) => {
    if (!query) return true;
    const haystack =
      item.kind === "task"
        ? [
            item.data.title,
            notificationLabel(item.data),
            item.data.actor_name ?? "",
          ]
        : [item.data.tender_title, item.data.sender_name ?? "", item.data.message ?? ""];
    return haystack.join(" ").toLowerCase().includes(query);
  };

  const filtered = sortedUnified.filter(
    (item) => (tab === "all" || item.category === tab) && matchesQuery(item)
  );
  const unified = filtered.slice(0, visibleCount);
  const hiddenCount = filtered.length - unified.length;
  const canLoadOlder = !fullHistory && olderCount > 0 && hiddenCount === 0;

  function renderItem(item: UnifiedNotif) {
    const isPinned = item.data.is_pinned;
    const isUnread = !item.data.is_read;

    if (item.kind === "pricing") {
      const n = item.data;
      return (
        <div
          key={`pricing-${n.id}`}
          // Pełna treść w dymku nad CAŁYM wierszem, nie tylko nad uciętym tekstem —
          // przyciski po prawej mają własne dymki, które ten przykrywają.
          title={[`${n.tender_title} — Tura ${n.round_number}`, n.message ? `„${n.message}"` : ""]
            .filter(Boolean)
            .join("\n")}
          className={`flex items-stretch group border-b border-[var(--color-border-muted)] last:border-0 transition-colors ${
            isPinned
              ? "border-l-2 border-l-amber-400 bg-amber-50/40 hover:bg-amber-50/70"
              : isUnread
              ? "bg-[var(--color-primary-dim)] hover:bg-[var(--color-primary-dim)]"
              : "hover:bg-[var(--color-bg-elevated)]"
          }`}
        >
          <a
            href={`/tenders/${n.tender_id}`}
            onClick={(e) => {
              e.preventDefault();
              if (!n.is_read) markPricingRead(n.id);
              setOpen(false);
              navigate(`/tenders/${n.tender_id}`);
            }}
            onAuxClick={(e) => {
              if (e.button === 1 && !n.is_read) markPricingRead(n.id);
            }}
            className="flex-1 text-left px-4 py-3 flex gap-3 min-w-0"
          >
            <div className="flex-shrink-0 flex flex-col items-center gap-1 w-5 pt-0.5">
              <TrendingUp size={13} className="text-[var(--color-text-secondary)]" strokeWidth={2} />
              {isUnread && <div className="w-1 h-1 bg-[var(--color-primary)]" />}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-xs leading-snug truncate ${isUnread ? "font-semibold text-[var(--color-text-primary)]" : "font-medium text-[var(--color-text-secondary)]"}`}
              >
                {n.tender_title} — Tura {n.round_number}
              </p>
              <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
                Wycena: <span className="font-mono">{formatAmount(n.proposal_value)} PLN</span>
                {n.sender_name && <span> · {n.sender_name}</span>}
              </p>
              {n.message && <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 italic truncate">„{n.message}"</p>}
              <p className="text-[10px] text-[var(--color-text-muted)] mt-0.5 font-mono">{timeAgo(n.created_at)}</p>
            </div>
          </a>
          <div className="flex-shrink-0 self-center mr-3 flex items-center">
            {isUnread && (
              <button
                onClick={(e) => { e.stopPropagation(); markPricingRead(n.id); }}
                className="p-1.5 text-[var(--color-border-strong)] hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-all"
                title="Oznacz jako przeczytane"
              >
                <Check size={12} strokeWidth={2.5} />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); togglePricingPin(n.id); }}
              className={`p-1.5 transition-all ${
                isPinned
                  ? "text-amber-500 hover:text-amber-700"
                  : "text-[var(--color-border-strong)] hover:text-[var(--color-text-secondary)] opacity-0 group-hover:opacity-100"
              }`}
              title={isPinned ? "Odepnij" : "Przypnij"}
            >
              <Pin size={12} className={isPinned ? "fill-amber-400" : ""} />
            </button>
          </div>
        </div>
      );
    }

    const n = item.data;
    const Icon = TYPE_ICON[n.notification_type] ?? Bell;
    const iconColor = TYPE_ICON_COLOR[n.notification_type] ?? "text-[var(--color-text-muted)]";
    const resolvedUrl = n.url
      ? (!isInternal && n.url.startsWith("/orders/")
          ? n.url.replace("/orders/", "/contractor/orders/")
          : n.url)
      : undefined;

    return (
      <div
        key={`task-${n.id}`}
        // Pełny tytuł w dymku nad CAŁYM wierszem (jak przy wycenach wyżej).
        title={n.title || notificationLabel(n)}
        className={`flex items-stretch group border-b border-[var(--color-border-muted)] last:border-0 transition-colors ${
          isPinned
            ? "border-l-2 border-l-amber-400 bg-amber-50/40 hover:bg-amber-50/70"
            : isUnread
            ? "bg-[var(--color-primary-dim)] hover:bg-[var(--color-primary-dim)]"
            : "hover:bg-[var(--color-bg-elevated)]"
        }`}
      >
        <a
          href={resolvedUrl}
          onClick={(e) => {
            e.preventDefault();
            if (!n.is_read) markRead(n.id);
            if (resolvedUrl) {
              setOpen(false);
              navigate(resolvedUrl);
            }
          }}
          onAuxClick={(e) => {
            if (e.button === 1 && !n.is_read) markRead(n.id);
          }}
          className="flex-1 text-left px-4 py-3 flex gap-3 min-w-0"
        >
          <div className="flex-shrink-0 flex flex-col items-center gap-1 w-5 pt-0.5">
            <Icon size={13} className={iconColor} strokeWidth={2} />
            {isUnread && <div className="w-1 h-1 bg-[var(--color-primary)]" />}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className={`text-xs leading-snug truncate ${isUnread ? "font-semibold text-[var(--color-text-primary)]" : "font-medium text-[var(--color-text-secondary)]"}`}
            >
              {n.title || notificationLabel(n)}
            </p>
            <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 uppercase tracking-wide font-mono" style={{ fontSize: "10px" }}>
              {notificationLabel(n)}
              {n.actor_name && (
                <span className="normal-case tracking-normal"> · {n.actor_name}</span>
              )}
              {(n.group_count ?? 1) > 1 && (
                <span className="normal-case tracking-normal"> · {eventCountLabel(n.group_count!)}</span>
              )}
            </p>
            {/* Kopia z przekazania powiadomień — dzwonek mówi, czyja to sprawa. */}
            {(n.extra_data as { delegated_from?: { name?: string } })?.delegated_from?.name && (
              <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 italic">
                Przekazane od: {(n.extra_data as { delegated_from: { name: string } }).delegated_from.name}
              </p>
            )}
            {(n.notification_type === "leave_rejected" || n.notification_type === "remote_work_rejected") && (n.extra_data as { comment?: string })?.comment && (
              <p className="text-[11px] text-[var(--color-primary)] mt-0.5 italic">
                „{(n.extra_data as { comment?: string }).comment}"
              </p>
            )}
            {(n.notification_type === "pock_request_rejected" || n.notification_type === "pock_request_approved") &&
              (n.extra_data as { review_comment?: string })?.review_comment && (
              <p className={`text-[11px] mt-0.5 italic ${n.notification_type === "pock_request_rejected" ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"}`}>
                „{(n.extra_data as { review_comment?: string }).review_comment}"
              </p>
            )}
            {n.notification_type === "order_cancelled" && (
              <p className="text-[11px] mt-0.5 italic text-[var(--color-primary)]">
                {(n.extra_data as { terminated?: boolean }).terminated
                  ? "Zamawiający odstąpił od zamówienia"
                  : "Anulowane jako bezprzedmiotowe"}
                {(n.extra_data as { reason?: string }).reason
                  ? ` — „${(n.extra_data as { reason?: string }).reason}"`
                  : ""}
              </p>
            )}
            {n.notification_type === "order_acceptance_warning" && (
              <>
                <p className="text-[11px] mt-0.5 italic text-amber-700 dark:text-amber-400 whitespace-normal">
                  {acceptanceWarningLine(n.extra_data as AcceptanceNotificationExtra)}
                </p>
                {acceptanceContextLine(n.extra_data as AcceptanceNotificationExtra) && (
                  <p className="text-[11px] mt-0.5 text-[var(--color-text-muted)] whitespace-normal">
                    {acceptanceContextLine(n.extra_data as AcceptanceNotificationExtra)}
                  </p>
                )}
              </>
            )}
            {n.notification_type === "order_deleted" && (n.extra_data as { contractor?: string }).contractor && (
              <p className="text-[11px] mt-0.5 italic text-[var(--color-text-muted)]">
                Podwykonawca: {(n.extra_data as { contractor?: string }).contractor}
              </p>
            )}
            {n.notification_type === "order_result_file_rejected" && (
              <p className="text-[11px] mt-0.5 italic text-[var(--color-primary)]">
                {(n.extra_data as { automatic?: boolean }).automatic ? "Automatycznie: " : ""}
                {(n.extra_data as { file_name?: string }).file_name}
                {(n.extra_data as { line_item_name?: string }).line_item_name
                  ? ` · ${(n.extra_data as { line_item_name?: string }).line_item_name}`
                  : ""}
                {(n.extra_data as { review_comment?: string }).review_comment
                  ? ` — „${(n.extra_data as { review_comment?: string }).review_comment}"`
                  : ""}
              </p>
            )}
            {n.notification_type === "pock_rejected" &&
              (n.extra_data as { rejection_comment?: string })?.rejection_comment && (
              <p className="text-[11px] mt-0.5 italic text-[var(--color-primary)]">
                „{(n.extra_data as { rejection_comment?: string }).rejection_comment}"
              </p>
            )}
            <p className="text-[10px] text-[var(--color-text-muted)] mt-0.5 font-mono">{timeAgo(n.group_latest_at ?? n.created_at)}</p>
          </div>
        </a>
        <div className="flex-shrink-0 self-center mr-3 flex items-center">
          {isUnread && (
            <button
              onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
              className="p-1.5 text-[var(--color-border-strong)] hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-all"
              title="Oznacz jako przeczytane"
            >
              <Check size={12} strokeWidth={2.5} />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); togglePin(n.id); }}
            className={`p-1.5 transition-all ${
              isPinned
                ? "text-amber-500 hover:text-amber-700"
                : "text-[var(--color-border-strong)] hover:text-[var(--color-text-secondary)] opacity-0 group-hover:opacity-100"
            }`}
            title={isPinned ? "Odepnij" : "Przypnij"}
          >
            <Pin size={12} className={isPinned ? "fill-amber-400" : ""} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
        title="Powiadomienia"
      >
        <Bell size={18} strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] text-[9px] flex items-center justify-center font-bold px-0.5 leading-none font-mono ${
              // The bell only ever lives in the (red) dev top bar, so import.meta.env.DEV
              // reliably means "on the red bar" — invert the red badge so it stays legible.
              import.meta.env.DEV
                ? "bg-white text-[var(--color-primary)]"
                : "bg-[var(--color-primary)] text-white"
            }`}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-[2px] animate-overlay-in"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            // On the dev top bar the header remaps --color-text-* to white for
            // legibility on red; those vars cascade into this panel (a DOM child
            // of the bar) and would render its text white on a light panel. Reset
            // the vars to the :root defaults so the drawer always reads normally.
            style={
              {
                "--color-text-primary": "#0a0a0a",
                "--color-text-secondary": "#404040",
                "--color-text-muted": "#888888",
              } as CSSProperties
            }
            onClick={(e) => e.stopPropagation()}
            className="animate-drawer-in flex h-full w-full max-w-md flex-col bg-[var(--color-bg-surface)] border-l border-[var(--color-border-strong)] shadow-[-8px_0_28px_rgba(0,0,0,0.14)] overflow-hidden"
          >
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-5 py-3.5 bg-[var(--color-bg-elevated)] border-b border-[var(--color-border-strong)]">
            <div className="flex items-center gap-2.5">
              <Bell size={15} className="text-[var(--color-primary)]" strokeWidth={2} />
              <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--color-text-primary)] font-mono">
                Powiadomienia
              </span>
              {unreadCount > 0 && (
                <span className="text-[10px] font-bold bg-[var(--color-primary)] text-white px-1.5 py-0.5 leading-none font-mono">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {tabCounts[tab].unread > 0 && (
                <button
                  onClick={() => markAllRead(tab === "all" ? undefined : tab)}
                  className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                  title={
                    tab === "all"
                      ? "Oznacz wszystkie jako przeczytane"
                      : `Oznacz jako przeczytane w zakładce „${NOTIFICATION_CATEGORY_LABELS[tab]}"`
                  }
                >
                  <Check size={11} strokeWidth={2.5} />
                  Przeczytane
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                title="Zamknij"
              >
                <XIcon size={16} />
              </button>
            </div>
          </div>

          {/* Zakładki — kategorie ze zgłoszenia „Powiadomienia ELGW5 - kategorie" */}
          <div
            role="tablist"
            aria-label="Kategorie powiadomień"
            // Zawijanie, nie przewijanie w poziomie: sześć zakładek nie mieści się w
            // szerokości panelu, a schowana zakładka to znowu „nie widzę, co ważne".
            className="shrink-0 flex flex-wrap border-b border-[var(--color-border-strong)] bg-[var(--color-bg-surface)]"
          >
            {TABS.map(({ key, label }) => {
              const active = tab === key;
              const counts = tabCounts[key];
              return (
                <button
                  key={key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(key)}
                  className={`shrink-0 flex items-center gap-1.5 px-2.5 py-2 border-b-2 font-mono text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                    active
                      ? "border-[var(--color-primary)] text-[var(--color-text-primary)]"
                      : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]"
                  }`}
                >
                  {label}
                  <span className="font-normal text-[9px] tabular-nums text-[var(--color-text-muted)]">
                    {counts.total}
                  </span>
                  {counts.unread > 0 && (
                    <span className="bg-[var(--color-primary)] text-white text-[9px] font-bold leading-none px-1 py-[2px] tabular-nums">
                      {counts.unread}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Wyszukiwarka */}
          <div className="shrink-0 flex items-center gap-2 px-4 py-2 border-b border-[var(--color-border-muted)] bg-[var(--color-bg-surface)]">
            <Search size={12} className="text-[var(--color-text-muted)]" strokeWidth={2} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj w powiadomieniach…"
              aria-label="Szukaj w powiadomieniach"
              className="flex-1 bg-transparent text-xs text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)]"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                title="Wyczyść"
              >
                <XIcon size={12} />
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {unified.length === 0 ? (
              <p className="text-xs text-[var(--color-text-muted)] font-mono uppercase tracking-wider text-center py-10">
                {query
                  ? "Nic nie pasuje do frazy"
                  : tab === "all"
                  ? "Brak powiadomień"
                  : "Pusto w tej zakładce"}
              </p>
            ) : (
              <>
              {unified.flatMap((item, index) => {
                const stamp =
                  item.kind === "task"
                    ? item.data.group_latest_at ?? item.data.created_at
                    : item.data.created_at;
                const day = dayBucket(stamp);
                const previous = index > 0 ? unified[index - 1] : null;
                const previousStamp = previous
                  ? previous.kind === "task"
                    ? previous.data.group_latest_at ?? previous.data.created_at
                    : previous.data.created_at
                  : null;
                const nodes = [];
                if (!previousStamp || dayBucket(previousStamp) !== day) {
                  nodes.push(
                    <div
                      key={`sep-${day}-${index}`}
                      className="px-4 py-1.5 bg-[var(--color-bg-elevated)] border-b border-[var(--color-border-muted)] font-mono text-[9px] font-semibold uppercase tracking-widest text-[var(--color-text-muted)]"
                    >
                      {day}
                    </div>
                  );
                }
                nodes.push(renderItem(item));
                return nodes;
              })}
              </>
            )}
            {hiddenCount > 0 && (
              <button
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 text-[10px] uppercase tracking-widest font-mono text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] border-t border-[var(--color-border-muted)] transition-colors"
              >
                <ChevronDown size={12} strokeWidth={2.5} />
                Wczytaj więcej ({hiddenCount})
              </button>
            )}
            {canLoadOlder && (
              <button
                onClick={() => setFullHistory(true)}
                disabled={fullHistoryLoading}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 text-[10px] uppercase tracking-widest font-mono text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] border-t border-[var(--color-border-muted)] transition-colors disabled:opacity-60"
                title="Dzwonek pokazuje ostatnie 30 dni — starsze wczytują się na żądanie"
              >
                <History size={12} strokeWidth={2.5} />
                {fullHistoryLoading ? "Wczytywanie…" : `Wczytaj starsze (${olderCount})`}
              </button>
            )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
